/**
 * @module SettingsDeficitSelector
 * @description Adaptive Deficit Engine — Weight loss speed selector with
 * Mifflin-St Jeor BMR/TDEE calculation, 4 deficit tiers, dynamic macro
 * recalculation, and safety guard (1200 kcal floor).
 */
import React, { useState, useEffect, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Target, Flame, Skull, ShieldAlert, Zap, TrendingDown, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const SPEED_OPTIONS = [
  {
    key: '0.5', weekly: '0.5 lb', deficit: 250,
    label_fr: 'Déficit leger', label_en: 'Light deficit',
    desc_fr: 'Focus longévité & confort', desc_en: 'Focus longevity & comfort',
    icon: Target, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30',
    ring: 'ring-blue-500/40',
  },
  {
    key: '1.0', weekly: '1.0 lb', deficit: 500,
    label_fr: 'Déficit standard', label_en: 'Standard deficit',
    desc_fr: 'Le juste milieu', desc_en: 'The sweet spot',
    icon: TrendingDown, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30',
    ring: 'ring-green-500/40',
  },
  {
    key: '1.5', weekly: '1.5 lb', deficit: 750,
    label_fr: 'Déficit soutenu', label_en: 'Sustained deficit',
    desc_fr: 'Transformation rapide', desc_en: 'Fast transformation',
    icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30',
    ring: 'ring-orange-500/40',
  },
  {
    key: '2.0', weekly: '2.0 lb', deficit: 1000,
    label_fr: 'Mode Commando', label_en: 'Commando Mode',
    desc_fr: 'Déficit agressif — suivi strict', desc_en: 'Aggressive deficit — strict tracking',
    icon: Skull, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30',
    ring: 'ring-red-500/40',
  },
];

const SettingsDeficitSelector = memo(function SettingsDeficitSelector() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [currentSpeed, setCurrentSpeed] = useState(null);
  const [selectedSpeed, setSelectedSpeed] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/users/deficit/info');
        setCurrentSpeed(res.data.current_speed || null);
        setSelectedSpeed(res.data.current_speed || null);
        setHasData(res.data.has_required_data);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  const handleSelect = async (speedKey) => {
    setSelectedSpeed(speedKey);
    setLoading(true);
    try {
      const res = await api.post('/users/deficit/calculate', { deficit_speed: speedKey });
      setPreview(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur';
      toast.error(msg);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedSpeed) return;
    setApplying(true);
    try {
      const res = await api.put('/users/deficit/apply', { deficit_speed: selectedSpeed });
      setCurrentSpeed(selectedSpeed);
      setPreview(res.data);
      updateUser({
        ...user,
        deficit_speed: selectedSpeed,
        daily_calories: res.data.target_calories,
        protein_min_g: res.data.protein_min_g,
        protein_max_g: res.data.protein_max_g,
        carbs_target_g: res.data.carbs_g,
        fats_target_g: res.data.fats_g,
      });
      toast.success(fr ? 'Objectif de perte applique! Macros recalculees.' : 'Weight loss target applied! Macros recalculated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setApplying(false);
    }
  };

  if (!hasData) {
    return (
      <Card className="bg-card border-border" data-testid="deficit-selector-incomplete">
        <CardHeader>
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-primary" />
            {fr ? 'Objectif de Perte Hebdomadaire' : 'Weekly Weight Loss Target'}
          </CardTitle>
          <CardDescription className="text-yellow-400">
            {fr ? 'Completez votre profil (poids, taille, age, sexe, activite) pour débloquer le selecteur de déficit.' : 'Complete your profile to unlock the déficit selector.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="deficit-selector">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-primary" />
          {fr ? 'Objectif de Perte Hebdomadaire' : 'Weekly Weight Loss Target'}
        </CardTitle>
        <CardDescription>
          {fr ? 'Selectionne ta vitesse de perte de poids. Les macros seront recalculees automatiquement.' : 'Select your weight loss speed. Macros will be recalculated automatically.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Speed Options Grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="deficit-speed-grid">
          {SPEED_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isSelected = selectedSpeed === opt.key;
            const isCurrent = currentSpeed === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => handleSelect(opt.key)}
                data-testid={`deficit-speed-${opt.key}`}
                className={`relative p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                  isSelected
                    ? `${opt.bg} ${opt.border} ring-2 ${opt.ring} scale-[1.02]`
                    : 'bg-secondary/50 border-border hover:border-muted-foreground/40'
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-2 -right-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  </div>
                )}
                <Icon className={`w-5 h-5 mb-1.5 ${isSelected ? opt.color : 'text-muted-foreground'}`} />
                <p className={`font-heading text-sm font-bold ${isSelected ? opt.color : 'text-foreground'}`}>
                  -{opt.weekly}/{fr ? 'sem' : 'wk'}
                </p>
                <p className={`text-[10px] leading-tight mt-0.5 ${isSelected ? 'text-foreground/80' : 'text-muted-foreground'}`}>
                  {fr ? opt.label_fr : opt.label_en}
                </p>
                <p className="text-[9px] text-muted-foreground mt-0.5">
                  -{opt.deficit} kcal/{fr ? 'jour' : 'day'}
                </p>
              </button>
            );
          })}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">{fr ? 'Calcul en cours...' : 'Calculating...'}</span>
          </div>
        )}

        {/* Preview Results */}
        {preview && !loading && (
          <div className="space-y-3" data-testid="deficit-preview">
            {/* Safety Warning */}
            {preview.safety_applied && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="deficit-safety-warning">
                <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-red-400">
                    {fr ? 'Protection Santé Activée' : 'Health Protection Activated'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fr
                      ? `Ce deficit ferait tomber ton apport sous ${preview.safety_floor} kcal. Nous avons ajuste ton plan pour ta securite.`
                      : `This deficit would bring your intake below ${preview.safety_floor} kcal. We adjusted your plan for safety.`}
                  </p>
                </div>
              </div>
            )}

            {/* Macro Results */}
            <div className="p-4 rounded-xl bg-secondary/50 border border-border space-y-3">
              {/* BMR & TDEE */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>BMR: {preview.bmr} kcal</span>
                <ArrowRight className="w-3 h-3" />
                <span>TDEE: {preview.tdee} kcal</span>
                <ArrowRight className="w-3 h-3" />
                <span className="text-primary font-bold">{preview.target_calories} kcal</span>
              </div>

              {/* Main Macros */}
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Flame className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                  <p className="font-heading text-lg font-bold text-orange-400" data-testid="deficit-calories">{preview.target_calories}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">kcal</p>
                </div>
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Zap className="w-4 h-4 text-green-400 mx-auto mb-1" />
                  <p className="font-heading text-lg font-bold text-green-400" data-testid="déficit-protein">{preview.protein_g}g</p>
                  <p className="text-[9px] text-muted-foreground uppercase">{fr ? 'prot' : 'prot'}</p>
                </div>
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <p className="font-heading text-lg font-bold text-blue-400" data-testid="déficit-carbs">{preview.carbs_g}g</p>
                  <p className="text-[9px] text-muted-foreground uppercase">{fr ? 'gluc' : 'carbs'}</p>
                </div>
                <div className="p-2 rounded-lg bg-red-500/10">
                  <p className="font-heading text-lg font-bold text-red-400" data-testid="déficit-fats">{preview.fats_g}g</p>
                  <p className="text-[9px] text-muted-foreground uppercase">{fr ? 'lip' : 'fats'}</p>
                </div>
              </div>

              {/* ETA */}
              {preview.weeks_estimate > 0 && (
                <p className="text-xs text-center text-muted-foreground">
                  {fr
                    ? `${preview.weight_to_lose_lbs} lbs a perdre — ~${preview.weeks_estimate} semaines`
                    : `${preview.weight_to_lose_lbs} lbs to lose — ~${preview.weeks_estimate} weeks`}
                </p>
              )}
            </div>

            {/* Apply Button */}
            <Button
              onClick={handleApply}
              disabled={applying || selectedSpeed === currentSpeed}
              className="w-full bg-primary text-primary-foreground font-heading uppercase tracking-wider"
              data-testid="deficit-apply-btn"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              {selectedSpeed === currentSpeed
                ? (fr ? 'Déjà applique' : 'Already applied')
                : (fr ? 'Appliquer ce déficit' : 'Apply this déficit')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default SettingsDeficitSelector;
